'use client';

import React, { useState } from 'react';
import Image from 'next/image';
import { useOffers } from '@/hooks/useOffers';
import { useConcierge } from '@/hooks/useConcierge';
import { Button } from '../ui/Button';
import { AnimatedCounter } from '../ui/AnimatedCounter';
import {
  MapPin,
  ArrowRight,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  History,
  ChevronLeft,
  Banknote,
  FileText,
  ShieldCheck,
  Phone,
  Mail,
  Download,
  Printer,
  Loader2,
  User,
} from 'lucide-react';
import type { Offer, AssignedAgent, CreateOfferRequest } from '@/types/customer';
import { cn } from '@/lib/utils';

export const MyOffers: React.FC = () => {
  const { offers, isLoading, withdrawOffer, updateOffer } = useOffers();
  const { agent } = useConcierge();
  const [selectedOfferId, setSelectedOfferId] = useState<string | null>(null);
  const [showPastOffers, setShowPastOffers] = useState(false);
  const PAST_STATUSES = ['COMPLETED', 'REJECTED', 'WITHDRAWN_BY_BUYER', 'WITHDRAWN_BY_VENDOR', 'EXPIRED'];
  
  const displayOffers = showPastOffers
    ? offers.filter((o) => PAST_STATUSES.includes(o.status))
    : offers.filter((o) => !PAST_STATUSES.includes(o.status));

  const selectedOffer = offers.find((o) => o.id === selectedOfferId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-stone-400" />
      </div>
    );
  }

  if (selectedOffer) {
    return (
      <DealRoom
        offer={selectedOffer}
        agent={agent}
        onBack={() => setSelectedOfferId(null)}
        onWithdraw={withdrawOffer}
        onUpdate={updateOffer}
      />
    );
  }

  return (
    <div className="space-y-8 animate-[fadeIn_0.3s_ease-out] pb-12">
      <div className="flex flex-col md:flex-row justify-between items-end gap-4">
        <div>
          <h2 className="text-3xl font-semibold text-stone-900 tracking-tight">
            Active Negotiations
          </h2>
          <p className="text-stone-500 mt-2 text-lg font-light">
            Manage your current bids and track vendor responses.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            className={cn('hidden md:flex', showPastOffers && 'bg-stone-900 text-white border-stone-900')}
            onClick={() => setShowPastOffers(!showPastOffers)}
          >
            <History size={16} className="mr-2" /> {showPastOffers ? 'Active Offers' : 'Past Offers'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="hidden md:flex"
            onClick={() => {
              const csv = offers.map((o) =>
                `${o.id},${o.status},${o.price?.amount || 0},${o.price?.currency || 'GBP'},${o.createdAt},${o.updatedAt}`
              ).join('\n');
              const blob = new Blob(
                [`ID,Status,Amount,Currency,Created,Updated\n${csv}`],
                { type: 'text/csv' }
              );
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `offers-report-${new Date().toISOString().slice(0, 10)}.csv`;
              a.click();
              URL.revokeObjectURL(url);
            }}
          >
            <Download size={16} className="mr-2" /> Export Report
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {displayOffers.length > 0 ? (
          displayOffers.map((offer) => (
            <ActiveDealCard
              key={offer.id}
              offer={offer}
              onClick={() => setSelectedOfferId(offer.id)}
            />
          ))
        ) : (
          <EmptyState />
        )}
      </div>
    </div>
  );
};

// --- COMPONENT: Active Deal Card (Portfolio View) ---
const ActiveDealCard: React.FC<{ offer: Offer; onClick: () => void }> = ({ offer, onClick }) => {
  const isCountered = offer.status === 'COUNTERED';
  const isAccepted = offer.status === 'ACCEPTED';
  const isRejected = offer.status === 'REJECTED';

  const cardStyles = isCountered
    ? 'border-amber-200 hover:border-amber-400 hover:shadow-[0_20px_40px_-15px_rgba(245,158,11,0.2)]'
    : isAccepted
      ? 'border-emerald-200 hover:border-emerald-400 hover:shadow-[0_20px_40px_-15px_rgba(16,185,129,0.2)]'
      : isRejected
        ? 'border-stone-200 hover:border-red-200 hover:shadow-lg opacity-90 hover:opacity-100'
        : 'border-stone-200 hover:border-stone-400 hover:shadow-2xl';

  return (
    <div
      onClick={onClick}
      className={cn(
        'group bg-white rounded-[2rem] border shadow-sm hover:-translate-y-1 transition-all duration-500 cursor-pointer overflow-hidden flex flex-col h-[480px] relative',
        cardStyles
      )}
    >
      {/* Decorative Background for Accepted State */}
      {isAccepted && (
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/50 via-transparent to-transparent pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-700 z-0" />
      )}

      {/* Image Header */}
      <div className="h-56 relative overflow-hidden z-10 bg-gradient-to-br from-stone-700 to-stone-900">
        <div className="absolute inset-0 bg-gradient-to-t from-stone-900/90 via-stone-900/20 to-transparent opacity-90" />

        {/* Status Badge */}
        <div className="absolute top-4 right-4 z-20">
          {isCountered && (
            <div className="relative">
              <span className="absolute -top-1 -right-1 flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-amber-500" />
              </span>
              <div className="px-4 py-2 rounded-full flex items-center gap-2 bg-amber-500/95 text-white shadow-lg backdrop-blur-xl border border-amber-400/50 transition-transform duration-300 group-hover:scale-105 group-hover:rotate-1">
                <AlertCircle size={14} className="animate-pulse" />
                <span className="text-[10px] font-bold uppercase tracking-widest">
                  Action Required
                </span>
              </div>
            </div>
          )}

          {isAccepted && (
            <div className="px-4 py-2 rounded-full flex items-center gap-2 bg-emerald-600/95 text-white shadow-lg backdrop-blur-xl border border-emerald-500/50 group-hover:bg-emerald-500 transition-all duration-300 group-hover:scale-105">
              <CheckCircle2 size={14} />
              <span className="text-[10px] font-bold uppercase tracking-widest">Offer Accepted</span>
            </div>
          )}

          {isRejected && (
            <div className="px-4 py-2 rounded-full flex items-center gap-2 bg-red-500/95 text-white shadow-lg backdrop-blur-xl border border-red-400/50 group-hover:bg-red-500 transition-colors">
              <XCircle size={14} />
              <span className="text-[10px] font-bold uppercase tracking-widest">Offer Rejected</span>
            </div>
          )}

          {!isCountered && !isAccepted && !isRejected && (
            <div className="px-4 py-2 rounded-full flex items-center gap-2 bg-white/20 text-white shadow-lg backdrop-blur-xl border border-white/20">
              <span className="text-[10px] font-bold uppercase tracking-widest">{offer.status}</span>
            </div>
          )}
        </div>

        <div className="absolute bottom-6 left-6 right-6 text-white z-20">
          <div className="flex items-center gap-2 text-white/70 text-[10px] font-bold uppercase tracking-widest mb-2">
            <span className="bg-white/10 px-2 py-0.5 rounded backdrop-blur-md border border-white/10">
              ID: {offer.id.slice(0, 8).toUpperCase()}
            </span>
          </div>
          <h3 className="font-medium text-2xl leading-tight text-white mb-2 group-hover:text-white/90 transition-colors">
            Offer #{offer.id.slice(0, 8)}
          </h3>
          <p className="text-sm text-white/80 flex items-center gap-1.5 font-light">
            <MapPin size={14} /> Listing {offer.listingId?.slice(0, 8) || 'N/A'}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="p-8 flex-1 flex flex-col justify-between bg-white relative z-10">
        <div className="space-y-6">
          <div className="flex justify-between items-end border-b border-stone-100 pb-6">
            <div>
              <span
                className={cn(
                  'block text-[10px] font-bold uppercase tracking-widest mb-1',
                  isCountered ? 'text-amber-600' : 'text-stone-400'
                )}
              >
                {isCountered ? 'Counter Offer' : 'Your Offer'}
              </span>
              <span className="text-3xl font-light text-stone-900 tracking-tight">
                £{((offer.price?.amount || 0) / 1000000).toFixed(2)}m
              </span>
            </div>
            <div className="text-right">
              <span className="block text-[10px] font-bold text-stone-400 uppercase tracking-widest mb-1">
                Status
              </span>
              <span className="text-lg font-medium text-stone-500">{offer.status}</span>
            </div>
          </div>

          <div className="flex items-center gap-2 text-xs text-stone-500 font-medium">
            <Clock size={14} className="text-stone-300" />
            <span>Last updated {new Date(offer.updatedAt).toLocaleDateString()}</span>
          </div>
        </div>

        <div className="flex items-center justify-between text-stone-900 font-semibold text-sm group-hover:text-stone-600 transition-colors pt-4">
          <span className={isCountered ? 'text-amber-600' : ''}>
            {isCountered ? 'Respond to Counter' : 'Enter Deal Room'}
          </span>
          <div
            className={cn(
              'h-8 w-8 rounded-full flex items-center justify-center transition-all duration-300',
              isCountered
                ? 'bg-amber-50 text-amber-600 group-hover:bg-amber-500 group-hover:text-white group-hover:translate-x-1'
                : 'bg-stone-50 text-stone-900 group-hover:bg-stone-900 group-hover:text-white group-hover:translate-x-1'
            )}
          >
            <ArrowRight size={14} />
          </div>
        </div>
      </div>

      {/* Visual Progress Bar */}
      <div className="absolute bottom-0 left-0 w-full h-1 bg-stone-100 z-20">
        <div
          className={cn(
            'h-full transition-all duration-1000 ease-out',
            isAccepted
              ? 'w-full bg-emerald-500'
              : isCountered
                ? 'w-3/4 bg-amber-500'
                : isRejected
                  ? 'w-full bg-red-500'
                  : 'w-1/2 bg-stone-800'
          )}
        />
      </div>
    </div>
  );
};

// --- COMPONENT: Deal Room (Detailed Interaction View) ---
const DealRoom: React.FC<{
  offer: Offer;
  agent: AssignedAgent | null;
  onBack: () => void;
  onWithdraw: (offerId: string, reason?: string) => Promise<Offer | null>;
  onUpdate: (offerId: string, data: Partial<CreateOfferRequest>) => Promise<Offer | null>;
}> = ({ offer, agent, onBack, onWithdraw, onUpdate }) => {
  const [isWithdrawing, setIsWithdrawing] = useState(false);
  const [isModifying, setIsModifying] = useState(false);
  const [showModifyInput, setShowModifyInput] = useState(false);
  const [newAmount, setNewAmount] = useState('');

  const handleWithdraw = async () => {
    if (!confirm('Are you sure you want to withdraw this offer? This cannot be undone.')) return;
    setIsWithdrawing(true);
    try {
      const result = await onWithdraw(offer.id, 'Withdrawn by buyer');
      if (result) {
        onBack();
      }
    } finally {
      setIsWithdrawing(false);
    }
  };

  const handleModify = async () => {
    const amount = parseFloat(newAmount);
    if (isNaN(amount) || amount <= 0) return;
    setIsModifying(true);
    try {
      await onUpdate(offer.id, {
        priceAmount: amount,
        priceCurrency: offer.price?.currency || 'GBP',
      });
      setShowModifyInput(false);
      setNewAmount('');
    } finally {
      setIsModifying(false);
    }
  };

  return (
    <div className="animate-[fadeIn_0.3s_ease-out] pb-12">
      {/* Top Navigation Bar */}
      <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-stone-200 pb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="p-2 -ml-2 hover:bg-stone-100 rounded-full text-stone-400 hover:text-stone-900 transition-colors"
          >
            <ChevronLeft size={24} />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-semibold text-stone-900 tracking-tight">
                Offer #{offer.id.slice(0, 8)}
              </h1>
              <span
                className={cn(
                  'px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide border',
                  offer.status === 'COUNTERED'
                    ? 'bg-amber-50 border-amber-200 text-amber-700'
                    : offer.status === 'ACCEPTED'
                      ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                      : 'bg-stone-100 border-stone-200 text-stone-500'
                )}
              >
                {offer.status}
              </span>
            </div>
            <p className="text-stone-500 text-sm flex items-center gap-1.5 mt-1">
              <MapPin size={12} /> Listing {offer.listingId?.slice(0, 8) || 'N/A'}
              <span className="text-stone-300 mx-1">|</span>
              <span className="font-mono text-stone-400">
                ID: {offer.id.slice(0, 8).toUpperCase()}
              </span>
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            className="bg-white border-stone-200"
            onClick={() => window.print()}
          >
            <Printer size={16} className="mr-2 text-stone-400" /> Print Summary
          </Button>
          {agent?.phone ? (
            <a href={`tel:${agent.phone}`}>
              <Button variant="outline" size="sm" className="bg-white border-stone-200">
                <Phone size={16} className="mr-2 text-stone-400" /> Contact Agent
              </Button>
            </a>
          ) : agent?.email ? (
            <a href={`mailto:${agent.email}`}>
              <Button variant="outline" size="sm" className="bg-white border-stone-200">
                <Phone size={16} className="mr-2 text-stone-400" /> Contact Agent
              </Button>
            </a>
          ) : (
            <Button variant="outline" size="sm" className="bg-white border-stone-200 opacity-50" disabled>
              <Phone size={16} className="mr-2 text-stone-400" /> Contact Agent
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 min-h-[600px] items-start relative">
        {/* LEFT COLUMN: Property Context (Sticky) */}
        <div className="lg:col-span-3 space-y-6 lg:sticky lg:top-8">
          <div className="rounded-3xl overflow-hidden aspect-[4/3] border border-stone-200 shadow-sm relative group bg-gradient-to-br from-stone-700 to-stone-900" />

          <div className="bg-white rounded-3xl border border-stone-200 shadow-sm overflow-hidden">
            <div className="p-6 border-b border-stone-100 bg-stone-50/50">
              <h3 className="text-xs font-bold text-stone-400 uppercase tracking-widest flex items-center gap-2">
                <Banknote size={14} /> Financials
              </h3>
            </div>
            <div className="p-6 space-y-6">
              <div>
                <p className="text-stone-500 text-xs font-medium uppercase tracking-wide mb-1">
                  Your Offer
                </p>
                <p className="text-3xl font-light text-stone-900 tracking-tight">
                  £<AnimatedCounter end={offer.price?.amount || 0} />
                </p>
              </div>

              {offer.counterPrice && (
                <div>
                  <p className="text-stone-500 text-xs font-medium uppercase tracking-wide mb-1">
                    Counter Price
                  </p>
                  <p className="text-xl font-medium text-amber-600">
                    £{offer.counterPrice.amount.toLocaleString()}
                  </p>
                </div>
              )}

              <div className="pt-4 border-t border-stone-100">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-stone-500">Status</span>
                  <span className="font-semibold text-stone-900">{offer.status}</span>
                </div>
              </div>
            </div>
          </div>

          {offer.conditions && offer.conditions.length > 0 && (
            <div className="bg-white rounded-3xl border border-stone-200 shadow-sm p-6">
              <h3 className="text-xs font-bold text-stone-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                <ShieldCheck size={14} /> Conditions
              </h3>
              <div className="space-y-3">
                {offer.conditions.map((c, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 text-sm text-stone-700 bg-stone-50 p-2.5 rounded-xl border border-stone-100"
                  >
                    <CheckCircle2 size={16} className="text-stone-400 shrink-0" />
                    <span className="font-medium">{c}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* MIDDLE COLUMN: Timeline Feed */}
        <div className="lg:col-span-6 bg-white rounded-[2.5rem] border border-stone-200 shadow-sm flex flex-col min-h-[600px] overflow-hidden relative z-10">
          <div className="p-6 border-b border-stone-100 bg-white/80 backdrop-blur-md sticky top-0 z-20 flex justify-between items-center">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-stone-100 rounded-lg text-stone-500">
                <History size={18} />
              </div>
              <div>
                <h3 className="font-semibold text-stone-900">Negotiation Log</h3>
                <p className="text-xs text-stone-500">Official record of all interactions</p>
              </div>
            </div>
            <div className="text-xs text-stone-400 font-mono">
              REF: {offer.id.slice(0, 8).toUpperCase()}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-6 md:p-8 space-y-6 bg-stone-50/30">
            <div className="flex justify-center py-4">
              <div className="bg-stone-100 px-4 py-1.5 rounded-full text-[10px] font-bold text-stone-500 uppercase tracking-wider flex items-center gap-2 border border-stone-200">
                Offer Created {new Date(offer.createdAt).toLocaleDateString()}
              </div>
            </div>

            <div className="flex justify-end">
              <div className="max-w-[85%] flex flex-col items-end">
                <div className="flex items-center gap-2 mb-1.5 px-1">
                  <span className="text-[10px] font-bold text-stone-400 uppercase tracking-widest">
                    You
                  </span>
                  <span className="text-stone-300">•</span>
                  <span className="text-[10px] text-stone-400 font-medium">
                    {new Date(offer.createdAt).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </div>

                <div className="p-6 rounded-2xl border relative shadow-sm bg-stone-900 text-white border-stone-900 rounded-tr-none">
                  <div className="mb-3">
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md bg-white/10 text-white/90">
                      Offer Submitted
                    </span>
                  </div>
                  <div className="mb-2">
                    <span className="text-3xl font-light tracking-tight text-white">
                      £{(offer.price?.amount || 0).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {offer.status === 'COUNTERED' && offer.counterPrice && (
              <div className="flex justify-start">
                <div className="max-w-[85%] flex flex-col items-start">
                  <div className="flex items-center gap-2 mb-1.5 px-1">
                    <span className="text-[10px] font-bold text-stone-400 uppercase tracking-widest">
                      Vendor Agent
                    </span>
                  </div>
                  <div className="p-6 rounded-2xl border relative shadow-sm bg-white text-stone-900 border-stone-200 rounded-tl-none">
                    <div className="mb-3">
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md bg-stone-100 text-stone-600">
                        Counter Received
                      </span>
                    </div>
                    <div className="mb-2">
                      <span className="text-3xl font-light tracking-tight text-stone-900">
                        £{offer.counterPrice.amount.toLocaleString()}
                      </span>
                    </div>
                    {offer.statusReason && (
                      <p className="text-sm leading-relaxed text-stone-600">
                        {offer.statusReason}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Action Area */}
          <div className="p-6 bg-white border-t border-stone-100 sticky bottom-0 z-20 shadow-[0_-10px_40px_rgba(0,0,0,0.03)]">
            {showModifyInput ? (
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-stone-700">New Amount (£):</span>
                  <input
                    type="number"
                    value={newAmount}
                    onChange={(e) => setNewAmount(e.target.value)}
                    placeholder={String(offer.price?.amount || 0)}
                    className="flex-1 border border-stone-200 rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-stone-900/10"
                    min="0"
                    step="1000"
                  />
                </div>
                <div className="flex gap-3">
                  <Button
                    fullWidth
                    size="lg"
                    className="bg-stone-900 hover:bg-black text-white"
                    onClick={handleModify}
                    disabled={!newAmount || isModifying}
                  >
                    {isModifying ? <Loader2 size={18} className="animate-spin mr-2" /> : null}
                    Submit Updated Offer
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    className="bg-white border-stone-200"
                    onClick={() => { setShowModifyInput(false); setNewAmount(''); }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col md:flex-row gap-3">
                <Button
                  fullWidth
                  size="lg"
                  className="bg-stone-900 hover:bg-black text-white shadow-xl shadow-stone-200"
                  disabled={offer.status === 'ACCEPTED' || offer.status === 'REJECTED'}
                  onClick={() => setShowModifyInput(true)}
                >
                  {offer.status === 'COUNTERED' ? 'Review & Counter' : 'Modify Offer'}
                  <ArrowRight size={18} className="ml-2" />
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="bg-white border-stone-200 text-red-600 hover:bg-red-50 hover:border-red-200"
                  disabled={offer.status === 'REJECTED' || isWithdrawing}
                  onClick={handleWithdraw}
                >
                  {isWithdrawing ? <Loader2 size={18} className="animate-spin mr-2" /> : null}
                  Withdraw
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT COLUMN: Agent & Insights (Sticky) */}
        <div className="lg:col-span-3 space-y-6 lg:sticky lg:top-8">
          {/* Agent Card */}
          <div className="bg-stone-900 rounded-3xl p-6 text-white shadow-xl shadow-stone-200">
            <div className="flex items-center gap-4 mb-6">
              <div className="relative">
                {agent?.avatarUrl ? (
                  <Image
                    src={agent.avatarUrl}
                    width={56}
                    height={56}
                    className="w-14 h-14 rounded-full border-2 border-stone-700 object-cover"
                    alt={agent.name || 'Agent'}
                  />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-stone-800 border-2 border-stone-700 flex items-center justify-center">
                    <User size={24} className="text-stone-400" />
                  </div>
                )}
                {agent?.isOnline && (
                  <div className="absolute -bottom-1 -right-1 bg-green-500 w-4 h-4 rounded-full border-2 border-stone-900" />
                )}
              </div>
              <div>
                <p className="font-semibold text-base">{agent?.name || 'Your Agent'}</p>
                <p className="text-xs text-stone-400 uppercase tracking-wider">{agent?.title || 'Lead Negotiator'}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {agent?.phone ? (
                <a href={`tel:${agent.phone}`}>
                  <Button
                    fullWidth
                    variant="secondary"
                    size="sm"
                    className="bg-stone-800 text-white hover:bg-stone-700 border-stone-700 h-10 text-xs"
                  >
                    <Phone size={14} className="mr-2" /> Call
                  </Button>
                </a>
              ) : (
                <Button
                  fullWidth
                  variant="secondary"
                  size="sm"
                  className="bg-stone-800 text-stone-500 border-stone-700 h-10 text-xs opacity-50 cursor-not-allowed"
                  disabled
                >
                  <Phone size={14} className="mr-2" /> Call
                </Button>
              )}
              {agent?.email ? (
                <a href={`mailto:${agent.email}`}>
                  <Button
                    fullWidth
                    variant="secondary"
                    size="sm"
                    className="bg-stone-800 text-white hover:bg-stone-700 border-stone-700 h-10 text-xs"
                  >
                    <Mail size={14} className="mr-2" /> Email
                  </Button>
                </a>
              ) : (
                <Button
                  fullWidth
                  variant="secondary"
                  size="sm"
                  className="bg-stone-800 text-stone-500 border-stone-700 h-10 text-xs opacity-50 cursor-not-allowed"
                  disabled
                >
                  <Mail size={14} className="mr-2" /> Email
                </Button>
              )}
            </div>
          </div>

          {/* Documents / Files */}
          <div className="bg-white rounded-3xl p-6 border border-stone-200 shadow-sm">
            <h4 className="text-xs font-bold text-stone-900 uppercase tracking-widest mb-4">
              Deal Documents
            </h4>
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 rounded-xl hover:bg-stone-50 transition-colors border border-transparent hover:border-stone-100 cursor-pointer group">
                <div className="p-2 bg-stone-100 rounded-lg text-stone-500 group-hover:text-stone-900 group-hover:bg-white border border-stone-200/50">
                  <FileText size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-stone-900 truncate">Official Offer.pdf</p>
                  <p className="text-xs text-stone-400">1.2 MB</p>
                </div>
                <Download size={16} className="text-stone-300 group-hover:text-stone-900" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const EmptyState = () => (
  <div className="col-span-full py-20 text-center">
    <div className="w-24 h-24 bg-stone-50 rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm border border-stone-100">
      <FileText size={40} className="text-stone-300" />
    </div>
    <h3 className="text-2xl font-bold text-stone-900 mb-2">No Active Negotiations</h3>
    <p className="text-stone-500 max-w-md mx-auto mb-8 text-lg font-light">
      You are not currently in any active negotiations. Once you make an offer on a property, track
      its progress here in real-time.
    </p>
    <Button size="lg" className="px-8 h-12">
      Browse Properties
    </Button>
  </div>
);
